import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import api from "services/api";
import PhoneInput from "components/PhoneInput";
import CountrySelect from "components/CountrySelect";
import DatePicker from "elements/DatePicker";
import { __ } from "services/Translator";
import ModalCloseButton from "components/ModalCloseButton";
import { normalizeStatesResponse, normalizeCountryCode, fetchIpCountry } from "utils/registrationStates";
import {
  dateToProfileParts,
  parsePhoneForProfile,
  profilePartsToDate,
  sanitizeTextInput,
  validateCity,
  validateDateOfBirth,
  validateEmailField,
  validatePersonName,
  validatePostalCode,
  validateStreetAddress,
} from "utils/profileValidation";


function ProfileField({ id, label, required, error, hint, children }) {
  return (
    <div
      className={
        "profile-complete-field input_item" + (error ? " profile-complete-field--error" : "")
      }
    >
      <label className="profile-complete-field__label name" htmlFor={id}>
        {label}
        {required ? <span className="requried_icon">*</span> : null}
      </label>
      {children}
      {hint && !error ? <p className="profile-complete-field__hint">{hint}</p> : null}
      {error ? (
        <p className="profile-complete-field__error errorMsg" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function ProfileSection({ icon, title, description, children, className = "" }) {
  return (
    <section className={`profile-complete-section ${className}`.trim()}>
      <div className="profile-complete-section__head">
        <span className="profile-complete-section__icon" aria-hidden="true">
          <i className={icon} />
        </span>
        <div>
          <h3 className="profile-complete-section__title">{title}</h3>
          {description ? (
            <p className="profile-complete-section__desc">{description}</p>
          ) : null}
        </div>
      </div>
      <div className="profile-complete-section__mobile-label" aria-hidden="true">
        {title}
      </div>
      <div className="profile-complete-section__body">{children}</div>
    </section>
  );
}

function LoadingSkeleton() {
  return (
    <div className="profile-complete-skeleton" aria-hidden="true">
      {[1, 2, 3].map((block) => (
        <div key={block} className="profile-complete-skeleton__block">
          <span className="profile-complete-skeleton__line profile-complete-skeleton__line--short" />
          <span className="profile-complete-skeleton__line" />
          <span className="profile-complete-skeleton__line" />
        </div>
      ))}
    </div>
  );
}

export default function ProfileCompleteModal({
  show,
  onHide,
  setSpinner,
  onCompleted,
  getAccountProgress,
  blocking = false,
}) {
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    dateOfBirth: null,
    address: "",
    city: "",
    state: "",
    zip: "",
    playerCountry: "",
  });
  const [errors, setErrors] = useState({});
  const [ipCountry, setIpCountry] = useState("");
  const [phoneInfo, setPhoneInfo] = useState({ e164: "", isValid: false });
  const [states, setStates] = useState([]);
  const [statesLoading, setStatesLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const suggestedCountry = ipCountry;

  const getMaxDateOfBirth = () => {
    const d = new Date();
    return new Date(d.getFullYear() - 18, d.getMonth(), d.getDate());
  };

  const loadProfile = async () => {
    setIsLoading(true);
    try {
      const response = await api.get("/profile");
      if (response.status !== 200 || !response.data) return;
      const data = response.data;
      setProfile(data);
      setForm({
        firstName: data.firstName || "",
        lastName: data.lastName || "",
        email: data.email || "",
        dateOfBirth: profilePartsToDate(data),
        address: data.address || "",
        city: data.city || "",
        state: data.state || "",
        zip: data.zip || "",
        playerCountry: normalizeCountryCode(data.playerCountry) || suggestedCountry || "",
      });
      if (data.phone && data.phoneCode) {
        const digits = String(data.phone).replace(/\D/g, "");
        const code = String(data.phoneCode).replace(/\D/g, "");
        setPhoneInfo({ e164: `+${code}${digits}`, isValid: true });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getIpCountry = async () => {
    setIpCountry(await fetchIpCountry(api));
  };

  useEffect(() => {
    if (!show) return;
    setErrors({});
    setSubmitError("");
    getIpCountry();
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  useEffect(() => {
    if (!form.playerCountry && suggestedCountry) {
      setForm((prev) => ({ ...prev, playerCountry: suggestedCountry, state: "" }));
    }
  }, [suggestedCountry, form.playerCountry]);

  const getStates = async (countryCode) => {
    if (!countryCode) {
      setStates([]);
      return;
    }
    setStatesLoading(true);
    try {
      const response = await api.get("/states/" + countryCode);
      if (response.status === 200) {
        setStates(normalizeStatesResponse(response.data));
      } else {
        setStates([]);
      }
    } catch (_) {
      setStates([]);
    } finally {
      setStatesLoading(false);
    }
  };

  useEffect(() => {
    getStates(form.playerCountry);
  }, [form.playerCountry]);

  const requiresState = useMemo(() => states.length > 0, [states.length]);

  const completionPercent = useMemo(() => {
    const checks = [
      Boolean(form.firstName?.trim()),
      Boolean(form.lastName?.trim()),
      Boolean(form.email?.trim()),
      Boolean(form.dateOfBirth),
      Boolean(phoneInfo.isValid && phoneInfo.e164),
      Boolean(form.playerCountry),
      Boolean(form.address?.trim()),
      Boolean(form.city?.trim()),
      Boolean(form.zip?.trim()),
      requiresState ? Boolean(String(form.state || "").trim()) : true,
    ];
    const done = checks.filter(Boolean).length;
    return Math.round((done / checks.length) * 100);
  }, [form, phoneInfo, requiresState]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
    setSubmitError("");
  };

  const handleCountryChange = (info) => {
    const code = normalizeCountryCode(info?.code);
    setForm((prev) => ({ ...prev, playerCountry: code, state: "" }));
    setErrors((prev) => ({ ...prev, playerCountry: "", state: "", zip: "" }));
  };

  const scrollToFirstError = () => {
    requestAnimationFrame(() => {
      const el = document.querySelector(
        ".profile-complete-modal .profile-complete-field--error"
      );
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  };

  const validateForm = () => {
    const nextErrors = {};

    const firstName = validatePersonName(form.firstName, "first name");
    if (!firstName.valid) nextErrors.firstName = firstName.error;

    const lastName = validatePersonName(form.lastName, "last name");
    if (!lastName.valid) nextErrors.lastName = lastName.error;

    const emailCheck = validateEmailField(form.email);
    if (!emailCheck.valid) nextErrors.email = emailCheck.error;

    const dobCheck = validateDateOfBirth(form.dateOfBirth);
    if (!dobCheck.valid) nextErrors.dateOfBirth = dobCheck.error;

    if (!phoneInfo.isValid || !phoneInfo.e164) {
      nextErrors.phone = __("Please enter a valid phone number");
    }

    const street = validateStreetAddress(form.address);
    if (!street.valid) nextErrors.address = street.error;

    const city = validateCity(form.city);
    if (!city.valid) nextErrors.city = city.error;

    if (!form.playerCountry) {
      nextErrors.playerCountry = __("Please select your country");
    }

    if (requiresState && !String(form.state || "").trim()) {
      nextErrors.state = __("Please select your region/state/province");
    }

    const postal = validatePostalCode(form.zip, form.playerCountry);
    if (!postal.valid) nextErrors.zip = postal.error;

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      scrollToFirstError();
      return;
    }

    const phoneParsed = parsePhoneForProfile(phoneInfo.e164, form.playerCountry || "US");
    if (!phoneParsed.valid) {
      setErrors((prev) => ({ ...prev, phone: phoneParsed.error }));
      scrollToFirstError();
      return;
    }

    const firstNameVal = validatePersonName(form.firstName, "first name");
    const lastNameVal = validatePersonName(form.lastName, "last name");
    const streetVal = validateStreetAddress(form.address);
    const cityVal = validateCity(form.city);
    const postalVal = validatePostalCode(form.zip, form.playerCountry);

    const dobParts = dateToProfileParts(form.dateOfBirth);
    const payload = {
      ...profile,
      firstName: firstNameVal.value,
      lastName: lastNameVal.value,
      email: sanitizeTextInput(form.email, 250).toLowerCase(),
      address: streetVal.value,
      city: cityVal.value,
      state: sanitizeTextInput(form.state, 80),
      zip: postalVal.value,
      playerCountry: form.playerCountry,
      phoneCode: phoneParsed.phoneCode,
      phone: phoneParsed.phone,
      ...dobParts,
    };

    setSpinner?.(true);
    setSubmitError("");
    try {
      const response = await api.post("/profile/save-basic", payload);
      if (response.status === 200) {
        onCompleted?.();
        onHide?.();
        if (getAccountProgress) getAccountProgress();
      } else if (response.data?.errors) {
        setErrors(response.data.errors);
        scrollToFirstError();
      } else {
        setSubmitError(response.data?.message || __("Something went wrong. Please try again."));
      }
    } catch (err) {
      setSubmitError(err?.response?.data?.message || __("Something went wrong. Please try again."));
    } finally {
      setSpinner?.(false);
    }
  };

  const handleSkip = () => {
    if (blocking) return;
    onHide?.();
  };

  return (
    <Modal
      show={show}
      onHide={handleSkip}
      centered
      size="lg"
      className="loginRegModal profile-complete-modal"
      dialogClassName="profile-complete-modal__dialog"
      fullscreen="md-down"
      backdrop="static"
      keyboard={!blocking}
      aria-labelledby="profile-complete-modal-title"
    >
      <Modal.Header className="profile-complete-modal__header">
        {!blocking && <ModalCloseButton onClick={onHide} />}
        <div className="profile-complete-modal__header-inner">
          <div className="profile-complete-modal__header-content">
            <span className="profile-complete-modal__badge" aria-hidden="true">
              <i className="fas fa-user-shield" />
            </span>
            <div className="profile-complete-modal__header-text">
              <Modal.Title id="profile-complete-modal-title">
                {__("Complete your profile")}
              </Modal.Title>
              <p className="profile-complete-modal__subtitle">
                {blocking
                  ? __(
                      "Please complete your details to continue to your deposit."
                    )
                  : __(
                      "Verify your account to unlock deposits, withdrawals, and promotions."
                    )}
              </p>
            </div>
          </div>
          {!isLoading && (
            <div className="profile-complete-modal__progress-wrap">
              <div className="profile-complete-modal__progress-meta">
                <span className="profile-complete-modal__progress-label">{__("Progress")}</span>
                <span className="profile-complete-modal__progress-value">
                  {completionPercent}%
                </span>
              </div>
              <div
                className="profile-complete-modal__progress-track"
                role="progressbar"
                aria-valuenow={completionPercent}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={__("Profile completion")}
              >
                <span
                  className="profile-complete-modal__progress-fill"
                  style={{ width: `${completionPercent}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </Modal.Header>

      <Modal.Body className="profile-complete-modal__body">
        {isLoading ? (
          <LoadingSkeleton />
        ) : (
          <form
            id="profile-complete-form"
            className="profile-complete-form form_area"
            onSubmit={handleSubmit}
            autoComplete="on"
            noValidate
          >
            <ProfileSection
              icon="fas fa-id-card"
              title={__("Personal details")}
              description={__("As on your ID.")}
            >
              <div className="row g-2 profile-complete-form__row">
                <div className="col-12 col-md-6">
                  <ProfileField id="profile-firstName" label={__("First name")} required error={errors.firstName}>
                    <input
                      id="profile-firstName"
                      type="text"
                      name="firstName"
                      className={"name_value " + (errors.firstName ? "error" : "")}
                      value={form.firstName}
                      onChange={(e) =>
                        updateField("firstName", sanitizeTextInput(e.target.value, 80))
                      }
                      placeholder={__("First name")}
                      autoComplete="given-name"
                    />
                  </ProfileField>
                </div>
                <div className="col-12 col-md-6">
                  <ProfileField id="profile-lastName" label={__("Last name")} required error={errors.lastName}>
                    <input
                      id="profile-lastName"
                      type="text"
                      name="lastName"
                      className={"name_value " + (errors.lastName ? "error" : "")}
                      value={form.lastName}
                      onChange={(e) =>
                        updateField("lastName", sanitizeTextInput(e.target.value, 80))
                      }
                      placeholder={__("Last name")}
                      autoComplete="family-name"
                    />
                  </ProfileField>
                </div>
              </div>

              <div className="row g-2 profile-complete-form__row">
                <div className="col-12 col-md-6">
                  <ProfileField
                    id="profile-email"
                    label={__("Email")}
                    required
                    error={errors.email}
                  >
                    <div className="profile-complete-email-wrap">
                      <input
                        id="profile-email"
                        type="email"
                        name="email"
                        className="name_value profile-complete-email-wrap__input"
                        value={form.email}
                        readOnly
                        disabled
                        aria-readonly="true"
                      />
                      <span className="profile-complete-email-wrap__lock" aria-hidden="true">
                        <i className="fas fa-lock" />
                      </span>
                    </div>
                  </ProfileField>
                </div>
                <div className="col-12 col-md-6">
                  <ProfileField
                    id="profile-dob"
                    label={__("Date of birth")}
                    required
                    error={errors.dateOfBirth}
                  >
                    <DatePicker
                      selected={form.dateOfBirth}
                      onChange={(date) => updateField("dateOfBirth", date)}
                      maxDate={getMaxDateOfBirth()}
                      showYearDropdown
                      scrollableYearDropdown
                      yearDropdownItemNumber={100}
                      dateFormat="yyyy-MM-dd"
                      placeholderText={__("Select your date of birth")}
                      className={"name_value " + (errors.dateOfBirth ? "error" : "")}
                    />
                  </ProfileField>
                </div>
              </div>
              <ProfileField id="profile-phone" label={__("Phone number")} required error={errors.phone}>
                <PhoneInput
                  key={form.playerCountry || suggestedCountry || "phone-default"}
                  defaultCountry={form.playerCountry || suggestedCountry || "US"}
                  value={phoneInfo.e164}
                  onChange={setPhoneInfo}
                  placeholder={__("Mobile number")}
                  containerClassName="phone_input_container profile-complete-phone"
                />
              </ProfileField>
            </ProfileSection>

            <ProfileSection
              icon="fas fa-map-marker-alt"
              title={__("Home address")}
              description={__("Use your billing address.")}
              className="profile-complete-section--address"
            >
              <ProfileField
                id="profile-country"
                label={__("Country")}
                required
                error={errors.playerCountry}
              >
                <CountrySelect
                  selected={form.playerCountry || suggestedCountry || null}
                  onChange={handleCountryChange}
                  placeholder={__("Search country...")}
                  variant="country"
                  panelClassName="country-select__panel--reg"
                />
              </ProfileField>

              <ProfileField
                id="profile-address"
                label={__("Address line 1")}
                required
                error={errors.address}
              >
                <input
                  id="profile-address"
                  type="text"
                  name="address"
                  className={"name_value " + (errors.address ? "error" : "")}
                  value={form.address}
                  onChange={(e) => updateField("address", sanitizeTextInput(e.target.value, 200))}
                  placeholder={__("House number and street")}
                  autoComplete="street-address"
                />
              </ProfileField>

              <div className="row g-2 profile-complete-form__row profile-complete-form__row--locality">
                <div className="col-12 col-md-6">
                  <ProfileField id="profile-city" label={__("City")} required error={errors.city}>
                    <input
                      id="profile-city"
                      type="text"
                      name="city"
                      className={"name_value " + (errors.city ? "error" : "")}
                      value={form.city}
                      onChange={(e) => updateField("city", sanitizeTextInput(e.target.value, 100))}
                      placeholder={__("City")}
                      autoComplete="address-level2"
                    />
                  </ProfileField>
                </div>
                <div className="col-12 col-md-6">
                  {requiresState ? (
                    <ProfileField
                      id="profile-state"
                      label={__("State / Province")}
                      required
                      error={errors.state}
                    >
                      <select
                        id="profile-state"
                        name="state"
                        className={"name_value " + (errors.state ? "error" : "")}
                        value={form.state}
                        onChange={(e) => updateField("state", e.target.value)}
                        disabled={!form.playerCountry || statesLoading}
                      >
                        <option value="">
                          {statesLoading ? __("Loading...") : __("Select state")}
                        </option>
                        {states.map((item) => (
                          <option key={item.value} value={item.value}>
                            {item.label}
                          </option>
                        ))}
                      </select>
                    </ProfileField>
                  ) : (
                    <ProfileField
                      id="profile-state"
                      label={__("State / Province")}
                      error={errors.state}
                    >
                      <input
                        id="profile-state"
                        type="text"
                        name="state"
                        className={"name_value " + (errors.state ? "error" : "")}
                        value={form.state}
                        onChange={(e) =>
                          updateField("state", sanitizeTextInput(e.target.value, 80))
                        }
                        placeholder={__("Region (optional)")}
                        autoComplete="address-level1"
                      />
                    </ProfileField>
                  )}
                </div>
              </div>

              <ProfileField
                id="profile-zip"
                label={__("Postal / Zip code")}
                required
                error={errors.zip}
              >
                <input
                  id="profile-zip"
                  type="text"
                  name="zip"
                  className={"name_value " + (errors.zip ? "error" : "")}
                  value={form.zip}
                  onChange={(e) => updateField("zip", sanitizeTextInput(e.target.value, 20))}
                  placeholder={__("Postal / ZIP code")}
                  autoComplete="postal-code"
                />
              </ProfileField>

            </ProfileSection>

          </form>
        )}
      </Modal.Body>

      <Modal.Footer className="profile-complete-modal__footer">
        <div className="profile-complete-modal__footer-panel">
          {submitError ? (
            <div className="profile-complete-modal__submit-error" role="alert">
              <i className="fas fa-exclamation-circle" aria-hidden="true" />
              <span>{submitError}</span>
            </div>
          ) : null}
          <p className="profile-complete-modal__secure-note">
            <i className="fas fa-shield-alt" aria-hidden="true" />
            {__("Your information is encrypted and stored securely.")}
          </p>
          <div className="profile-complete-modal__footer-actions">
            {!blocking && (
              <button
                type="button"
                className="profile-complete-modal__btn-skip"
                onClick={handleSkip}
                disabled={isLoading}
              >
                {__("Skip for now")}
              </button>
            )}
            <Button
              className="profile-complete-modal__btn-save commonBtn pTwoBtn"
              type="submit"
              form="profile-complete-form"
              disabled={isLoading}
            >
              {__("Save & continue")}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
